import { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../../context/AuthContext';
import OrderStatusBadge from '../../components/OrderStatusBadge';
import PageHeader from '../../components/ui/PageHeader';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import Avatar from '../../components/ui/Avatar';
import Modal from '../../components/ui/Modal';
import PhotoPlaceholder from '../../components/ui/PhotoPlaceholder';

function InfoRow({ icon, label, value }) {
  if (!value) return null;
  return (
    <div className="flex items-start gap-3">
      <span className="text-ink-400 mt-0.5 shrink-0">{icon}</span>
      <div>
        <p className="text-xs text-ink-400 mb-0.5">{label}</p>
        <p className="text-sm text-ink-800">{value}</p>
      </div>
    </div>
  );
}

function SectionCard({ label, children, className = '' }) {
  return (
    <div className={`bg-white border border-sand-200 rounded-lg p-4 mb-6 shadow-card ${className}`}>
      {label && (
        <p className="text-xs font-medium text-ink-400 uppercase tracking-wide mb-3">{label}</p>
      )}
      {children}
    </div>
  );
}

// Actions that change order status/flow, keyed by a pendingAction id.
// Each maps to a confirm-modal (title/bodyText/confirm/confirmVariant) and the
// PUT request to fire on confirmation.
const ACTION_CONFIG = {
  accepted: {
    endpoint: id => `/api/orders/${id}/status`,
    payload: { status: 'accepted' },
    title: 'Accept request',
    bodyText: 'Are you sure you want to accept this order? The customer will be notified.',
    confirm: 'Accept',
    confirmVariant: 'primary',
  },
  cancelled: {
    endpoint: id => `/api/orders/${id}/status`,
    payload: { status: 'cancelled' },
    title: 'Decline request',
    bodyText: 'Are you sure you want to decline this order? This action cannot be undone.',
    confirm: 'Decline',
    confirmVariant: 'danger',
  },
  completed: {
    endpoint: id => `/api/orders/${id}/status`,
    payload: { status: 'completed' },
    title: 'Mark as completed',
    bodyText: 'Confirm that this order has been completed. The customer will be notified.',
    confirm: 'Mark completed',
    confirmVariant: 'primary',
  },
  cancellation_confirm: {
    endpoint: id => `/api/orders/${id}/status`,
    payload: { status: 'cancelled' },
    title: 'Confirm cancellation',
    bodyText: "This will finalize the customer's cancellation request and mark the order as cancelled.",
    confirm: 'Confirm cancellation',
    confirmVariant: 'danger',
  },
  cancellation_requested: {
    endpoint: id => `/api/orders/${id}/request-cancellation`,
    payload: {},
    title: 'Cancel order',
    bodyText: 'Are you sure you want to cancel this order? The company will be notified and will process the cancellation. This cannot be undone once requested.',
    confirm: 'Cancel order',
    confirmVariant: 'danger',
  },
};

export default function OrderDetail() {
  const { id, token } = useParams();
  const isGuestView = Boolean(token);
  const { user } = useAuth();
  const navigate = useNavigate();
  const [order, setOrder] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(false);
  const [updating, setUpdating] = useState(false);
  const [pendingAction, setPendingAction] = useState(null);
  const [updateError, setUpdateError] = useState('');
  const [reviewOpen, setReviewOpen] = useState(false);
  const [reviewRating, setReviewRating] = useState(0);
  const [reviewComment, setReviewComment] = useState('');
  const [submittingReview, setSubmittingReview] = useState(false);
  const [reviewError, setReviewError] = useState('');

  const fetchOrder = () => {
    const request = isGuestView
      ? axios.get(`${process.env.REACT_APP_API_URL}/api/orders/guest/${token}`)
      : axios.get(`${process.env.REACT_APP_API_URL}/api/orders/${id}`, {
          headers: { Authorization: `Bearer ${localStorage.getItem('token')}` }
        });
    request
      .then(res => { setOrder(res.data.order); setLoadError(false); })
      .catch(() => setLoadError(true))
      .finally(() => setLoading(false));
  };

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { fetchOrder(); }, [id, token]);

  const confirmUpdate = async () => {
    setUpdating(true);
    setUpdateError('');
    try {
      const token = localStorage.getItem('token');
      const config = ACTION_CONFIG[pendingAction];
      await axios.put(
        `${process.env.REACT_APP_API_URL}${config.endpoint(id)}`,
        config.payload,
        { headers: { Authorization: `Bearer ${token}` } }
      );
      setPendingAction(null);
      fetchOrder();
    } catch (err) {
      setUpdateError(err.response?.data?.error || 'Failed to update. Please try again.');
    } finally {
      setUpdating(false);
    }
  };

  const submitReview = async () => {
    if (!reviewRating) {
      setReviewError('Please select a rating');
      return;
    }
    setSubmittingReview(true);
    setReviewError('');
    try {
      const authToken = localStorage.getItem('token');
      await axios.post(
        `${process.env.REACT_APP_API_URL}/api/orders/${id}/review`,
        { rating: reviewRating, comment: reviewComment.trim() || undefined },
        { headers: { Authorization: `Bearer ${authToken}` } }
      );
      setReviewOpen(false);
      fetchOrder();
    } catch (err) {
      setReviewError(err.response?.data?.error || 'Failed to submit review. Please try again.');
    } finally {
      setSubmittingReview(false);
    }
  };

  if (loading) return (
    <div className="flex justify-center items-center min-h-[60vh]">
      <div className="w-8 h-8 border-2 border-sand-200 border-t-terra-400 rounded-full animate-spin" />
    </div>
  );

  if (loadError || !order) return (
    <div className="flex flex-col items-center justify-center min-h-[60vh] gap-4 px-4 text-center">
      <p className="text-sm text-ink-400">This request could not be loaded. It may not exist, or you may not have access to it.</p>
      <Button variant="secondary" onClick={() => navigate('/')}>Back to home</Button>
    </div>
  );

  // A guest-tracking link is view-only for the guest themselves, regardless of who's
  // logged in (e.g. if the provider opens a link the guest shared with them) — never
  // grant provider/customer actions through it.
  const isProvider = !isGuestView && user?.id === order.provider_id;
  const isCustomer = !isGuestView && user?.id === order.customer_id;
  const other = isProvider
    ? (order.customer || { first_name: order.guest_first_name, last_name: order.guest_last_name, phone: order.guest_phone })
    : order.provider;
  const listing = order.listings;

  const formatAnswer = (a) => {
    if (a.question_type === 'boolean') return a.answer_boolean ? 'Yes' : 'No';
    if (a.question_type === 'datetime' && a.answer_datetime) {
      return new Date(a.answer_datetime).toLocaleString('en-AE', {
        day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit',
      });
    }
    if (a.question_type === 'number') return a.answer_number;
    return a.answer_text;
  };

  const modalConfig = ACTION_CONFIG[pendingAction] || null;

  const PhoneIcon = (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 6.75c0 8.284 6.716 15 15 15h2.25a2.25 2.25 0 002.25-2.25v-1.372c0-.516-.351-.966-.852-1.091l-4.423-1.106c-.44-.11-.902.055-1.173.417l-.97 1.293c-.282.376-.769.542-1.21.38a12.035 12.035 0 01-7.143-7.143c-.162-.441.004-.928.38-1.21l1.293-.97c.363-.271.527-.734.417-1.173L6.963 3.102a1.125 1.125 0 00-1.091-.852H4.5A2.25 2.25 0 002.25 4.5v2.25z" />
    </svg>
  );

  const WhatsAppIcon = (
    <svg className="w-4 h-4" fill="currentColor" viewBox="0 0 24 24">
      <path d="M12.04 2.003c-5.518 0-9.995 4.478-9.995 9.997 0 1.762.462 3.484 1.34 5.003L2 22l5.117-1.343a9.96 9.96 0 004.923 1.303h.004c5.518 0 9.995-4.478 9.995-9.997 0-2.67-1.04-5.18-2.93-7.07a9.935 9.935 0 00-7.07-2.89zm0 18.28h-.003a8.28 8.28 0 01-4.223-1.157l-.303-.18-3.037.797.81-2.96-.198-.304a8.284 8.284 0 01-1.27-4.42c0-4.582 3.73-8.312 8.315-8.312a8.26 8.26 0 015.878 2.437 8.26 8.26 0 012.435 5.88c0 4.582-3.73 8.219-8.404 8.219zm4.552-6.156c-.25-.125-1.475-.727-1.703-.81-.228-.084-.395-.125-.562.125-.166.25-.645.81-.79.976-.146.166-.29.187-.54.062-.25-.125-1.054-.388-2.008-1.238-.742-.662-1.244-1.48-1.39-1.73-.146-.25-.016-.385.11-.51.113-.112.25-.29.375-.437.125-.146.166-.25.25-.416.083-.166.041-.312-.02-.437-.063-.125-.563-1.354-.77-1.855-.203-.487-.409-.421-.563-.429-.145-.007-.312-.009-.478-.009a.916.916 0 00-.666.312c-.229.25-.874.854-.874 2.083s.895 2.417 1.02 2.583c.125.166 1.76 2.687 4.264 3.768.596.257 1.06.41 1.423.526.598.19 1.142.163 1.573.099.48-.072 1.475-.603 1.683-1.185.208-.583.208-1.083.146-1.187-.062-.104-.229-.166-.478-.291z" />
    </svg>
  );

  const HomeIcon = (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M2.25 12l8.954-8.955c.44-.439 1.152-.439 1.591 0L21.75 12M4.5 9.75v10.125c0 .621.504 1.125 1.125 1.125H9.75v-4.875c0-.621.504-1.125 1.125-1.125h2.25c.621 0 1.125.504 1.125 1.125V21h4.125c.621 0 1.125-.504 1.125-1.125V9.75M8.25 21h8.25" />
    </svg>
  );

  const PinIcon = (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M15 10.5a3 3 0 11-6 0 3 3 0 016 0z" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 10.5c0 7.142-7.5 11.25-7.5 11.25S4.5 17.642 4.5 10.5a7.5 7.5 0 1115 0z" />
    </svg>
  );

  const StarIcon = ({ filled }) => (
    <svg className="w-8 h-8" fill={filled ? 'currentColor' : 'none'} viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.5}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M11.48 3.499a.562.562 0 011.04 0l2.125 5.111a.563.563 0 00.475.345l5.518.442c.499.04.701.663.321.988l-4.204 3.602a.563.563 0 00-.182.557l1.285 5.385a.562.562 0 01-.84.61l-4.725-2.885a.562.562 0 00-.586 0L6.982 20.54a.562.562 0 01-.84-.61l1.285-5.386a.562.562 0 00-.182-.557l-4.204-3.602a.562.562 0 01.321-.988l5.518-.442a.563.563 0 00.475-.345L11.48 3.5z" />
    </svg>
  );

  const ClockIcon = (
    <svg className="w-4 h-4" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={1.8}>
      <path strokeLinecap="round" strokeLinejoin="round" d="M12 6v6l4 2" />
      <path strokeLinecap="round" strokeLinejoin="round" d="M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
    </svg>
  );

  const formattedScheduledAt = order.scheduled_at
    ? new Date(order.scheduled_at).toLocaleString('en-AE', {
        weekday: 'short', day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit',
      })
    : null;

  const formattedPlacedAt = order.created_at
    ? new Date(order.created_at).toLocaleDateString('en-AE', {
        day: 'numeric', month: 'short', year: 'numeric'
      })
    : null;

  const canModifyOrCancel = isCustomer && ['pending', 'accepted'].includes(order.status);

  return (
    <>
      {/* Confirmation Modal */}
      <Modal
        isOpen={!!pendingAction}
        onClose={() => { if (!updating) { setPendingAction(null); setUpdateError(''); } }}
        title={modalConfig?.title}
        footer={
          modalConfig ? (
            <>
              <Button
                variant="secondary"
                onClick={() => { setPendingAction(null); setUpdateError(''); }}
                disabled={updating}
              >
                Cancel
              </Button>
              <Button
                variant={modalConfig.confirmVariant}
                onClick={confirmUpdate}
                loading={updating}
              >
                {modalConfig.confirm}
              </Button>
            </>
          ) : null
        }
      >
        {modalConfig && (
          <div className="flex flex-col gap-3">
            <p className="text-sm text-ink-400 leading-relaxed">{modalConfig.bodyText}</p>
            {updateError && <p className="text-sm text-uae-red">{updateError}</p>}
          </div>
        )}
      </Modal>

      {/* Review Modal */}
      <Modal
        isOpen={reviewOpen}
        onClose={() => { if (!submittingReview) { setReviewOpen(false); setReviewError(''); } }}
        title="Rate your experience"
        footer={
          <>
            <Button
              variant="secondary"
              onClick={() => { setReviewOpen(false); setReviewError(''); }}
              disabled={submittingReview}
            >
              Cancel
            </Button>
            <Button variant="primary" onClick={submitReview} loading={submittingReview}>
              Submit review
            </Button>
          </>
        }
      >
        <div className="flex flex-col gap-4">
          <div>
            <p className="text-sm text-ink-700 font-medium mb-2">How happy were you with this service?</p>
            <div className="flex gap-1">
              {[1, 2, 3, 4, 5].map(n => (
                <button
                  key={n}
                  type="button"
                  aria-label={`${n} star${n > 1 ? 's' : ''}`}
                  onClick={() => setReviewRating(n)}
                  className={`transition-colors duration-150 ${n <= reviewRating ? 'text-terra-400' : 'text-sand-300 hover:text-sand-400'}`}
                >
                  <StarIcon filled={n <= reviewRating} />
                </button>
              ))}
            </div>
          </div>
          <Input
            as="textarea"
            rows={4}
            label="Tell us about the service (optional)"
            placeholder="What went well? What could be better?"
            value={reviewComment}
            onChange={e => setReviewComment(e.target.value)}
          />
          {reviewError && <p className="text-sm text-uae-red">{reviewError}</p>}
        </div>
      </Modal>

      <div className="bg-sand-50 min-h-screen">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8 py-8 sm:py-12">

          {/* Header */}
          <PageHeader title="Request Details" />
          <div className="mb-6">
            <OrderStatusBadge status={order.status} />
          </div>

          {/* Order item */}
          <SectionCard label="Order Item">
            <div className="flex gap-4 items-start">
              <div className="w-16 h-16 rounded-lg shrink-0 bg-sand-100 overflow-hidden">
                {listing?.cover_photo_url ? (
                  <img
                    src={listing.cover_photo_url}
                    alt={listing?.title || ''}
                    className="w-full h-full object-cover"
                  />
                ) : (
                  <PhotoPlaceholder small />
                )}
              </div>
              <div className="min-w-0">
                <p className="font-heading text-sm font-semibold text-ink-900 truncate">{order.title}</p>
                <p className="text-xs text-ink-400 mt-0.5">{listing?.category}</p>
                {order.price && (
                  <p className="text-sm font-semibold text-terra-400 mt-1">AED {order.price}</p>
                )}
              </div>
            </div>
            {order.message && (
              <div className="mt-4 pt-4 border-t border-sand-200">
                <p className="text-xs font-medium text-ink-400 uppercase tracking-wide mb-2">Message</p>
                <p className="text-sm text-ink-400 leading-relaxed whitespace-pre-wrap break-words">
                  {order.message}
                </p>
              </div>
            )}
          </SectionCard>

          {/* Customer / Provider info */}
          <SectionCard label={isProvider ? 'Customer' : 'Provider'}>
            <div className="flex items-center gap-3 mb-4">
              <Avatar
                src={other?.avatar_url}
                name={[other?.first_name, other?.last_name].filter(Boolean).join(' ')}
                size="md"
              />
              <p className="text-sm font-medium text-ink-900 truncate">
                {other?.first_name} {other?.last_name}
              </p>
            </div>
            <div className="flex flex-col gap-3">
              <InfoRow icon={PhoneIcon} label="Phone" value={other?.phone} />
              <InfoRow icon={PinIcon} label="City" value={order?.location} />
              <InfoRow icon={PinIcon} label="Address" value={order?.address_line} />
              <InfoRow icon={HomeIcon} label="Building" value={order?.building} />
              <InfoRow icon={HomeIcon} label="Unit" value={order?.unit} />
            </div>
            {isProvider && other?.phone && (
              <a
                href={`https://wa.me/${other.phone.replace(/\D/g, '')}`}
                target="_blank"
                rel="noreferrer"
                className="mt-4 w-full inline-flex justify-center items-center gap-2 bg-[#25D366] text-white px-4 py-2.5 rounded-lg text-sm font-medium hover:bg-[#1ebe5d] transition-colors duration-200"
              >
                {WhatsAppIcon}
                Message on WhatsApp
              </a>
            )}
          </SectionCard>

          {/* Appended questions */}
          {order.order_answers?.length > 0 && (
            <SectionCard label="Additional Details">
              <div className="flex flex-col gap-3">
                {order.order_answers.map(a => (
                  <div key={a.id}>
                    <p className="text-xs text-ink-400 mb-0.5">{a.question_label}</p>
                    <p className="text-sm text-ink-800">{formatAnswer(a)}</p>
                  </div>
                ))}
              </div>
            </SectionCard>
          )}

          {/* Timestamps */}
          <SectionCard label="Timestamps">
            <div className="flex flex-col gap-3">
              <InfoRow icon={ClockIcon} label="Service date & time" value={formattedScheduledAt} />
              <InfoRow icon={ClockIcon} label="Placed" value={formattedPlacedAt} />
            </div>
          </SectionCard>

          {/* Provider actions */}
          {isProvider && (
            <div className="flex flex-col sm:flex-row gap-3">
              {order.status === 'pending' && (
                <>
                  <Button
                    variant="primary"
                    className="w-full justify-center"
                    onClick={() => setPendingAction('accepted')}
                  >
                    Accept request
                  </Button>
                  <Button
                    variant="danger"
                    className="w-full justify-center"
                    onClick={() => setPendingAction('cancelled')}
                  >
                    Decline
                  </Button>
                </>
              )}
              {order.status === 'accepted' && (
                <Button
                  variant="primary"
                  className="w-full justify-center"
                  onClick={() => setPendingAction('completed')}
                >
                  Mark as completed
                </Button>
              )}
              {order.status === 'cancellation_requested' && (
                <Button
                  variant="danger"
                  className="w-full justify-center"
                  onClick={() => setPendingAction('cancellation_confirm')}
                >
                  Confirm cancellation
                </Button>
              )}
              {(order.status === 'completed' || order.status === 'cancelled') && (
                <p className="text-sm text-ink-400">This order is {order.status}.</p>
              )}
            </div>
          )}

          {/* Guest tracking view — status only, no account-holder actions available */}
          {isGuestView && (
            <div className="flex flex-col items-center gap-3 mt-6">
              {order.status === 'pending' && (
                <p className="text-sm text-ink-400 text-center">
                  Request sent. The provider will contact you on the phone number you provided.
                </p>
              )}
              {order.status === 'accepted' && (
                <p className="text-sm text-ink-400 text-center">
                  Your request has been accepted.
                </p>
              )}
              {order.status === 'cancellation_requested' && (
                <p className="text-sm text-ink-400 text-center">
                  Cancellation requested — we'll notify you once it's processed.
                </p>
              )}
              {(order.status === 'completed' || order.status === 'cancelled') && (
                <p className="text-sm text-ink-400 text-center">This order is {order.status}.</p>
              )}
              <p className="text-xs text-ink-400 text-center">
                Bookmark this page to check your request's status later.
              </p>
            </div>
          )}

          {/* Customer actions */}
          {isCustomer && (
            <div className="flex flex-col items-center gap-3 mt-6">
              {order.status === 'pending' && (
                <p className="text-sm text-ink-400 text-center">
                  Request sent. The provider will contact you soon.
                </p>
              )}
              {order.status === 'accepted' && (
                <p className="text-sm text-ink-400 text-center">
                  Your request has been accepted.
                </p>
              )}
              {order.status === 'cancellation_requested' && (
                <p className="text-sm text-ink-400 text-center">
                  Cancellation requested — we'll notify you once it's processed.
                </p>
              )}
              {canModifyOrCancel && (
                <div className="flex flex-col sm:flex-row gap-3 w-full">
                  <Button
                    variant="secondary"
                    className="w-full justify-center"
                    onClick={() => navigate(`/orders/${id}/modify`)}
                  >
                    Modify Order
                  </Button>
                  <Button
                    variant="danger"
                    className="w-full justify-center"
                    onClick={() => setPendingAction('cancellation_requested')}
                  >
                    Cancel Order
                  </Button>
                </div>
              )}
              {order.status === 'completed' && (
                order.review ? (
                  <div className="w-full">
                    <p className="text-sm text-ink-700 font-medium mb-2 text-center">Your review</p>
                    <div className="flex justify-center gap-1 mb-2">
                      {[1, 2, 3, 4, 5].map(n => (
                        <span key={n} className={n <= order.review.rating ? 'text-terra-400' : 'text-sand-300'}>
                          <StarIcon filled={n <= order.review.rating} />
                        </span>
                      ))}
                    </div>
                    {order.review.comment && (
                      <p className="text-sm text-ink-400 text-center leading-relaxed">{order.review.comment}</p>
                    )}
                  </div>
                ) : (
                  <Button
                    variant="primary"
                    className="w-full justify-center"
                    onClick={() => { setReviewRating(0); setReviewComment(''); setReviewError(''); setReviewOpen(true); }}
                  >
                    Rate your experience
                  </Button>
                )
              )}
            </div>
          )}
        </div>
      </div>
    </>
  );
}
